import {ChatMessageApiModel} from '../../api/models/chat-message.api.model';

const userKey = (user: ChatMessageApiModel['sender']): string => typeof user === 'string'
  ? user.split('/').filter(Boolean).pop() || '' : String(user?.id || user?.['@id']?.split('/').pop() || '');

/** Nested JSON realtime previews can contain an id without JSON-LD metadata. */
export function normalizeChatMessage(message: ChatMessageApiModel): ChatMessageApiModel {
  if (!message || message.localStatus) return message;
  const id = String(message.id || '').split('/').filter(Boolean).pop();
  const reactions = message.reactions?.map(reaction => {
    const iri = reaction['@id'] || (reaction.id ? `/api/reactions/${reaction.id}` : undefined);
    const subject = reaction.subject || message['@id'] || (id ? `/api/messages/${id}` : undefined);
    const authorIri = typeof reaction.author === 'string' ? reaction.author
      : reaction.author?.['@id'] || (reaction.author?.slug ? `/api/users/${reaction.author.slug}` : undefined);
    const author = typeof reaction.author !== 'string' && authorIri && !reaction.author?.['@id']
      ? {...reaction.author, '@id': authorIri} : reaction.author;
    if (iri === reaction['@id'] && subject === reaction.subject && author === reaction.author) return reaction;
    return {...reaction, '@id': iri, subject, author};
  });
  const reactionsChanged = reactions?.some((reaction, index) => reaction !== message.reactions[index]);
  if ((message['@id'] || !id) && !reactionsChanged) return message;
  return {...message, ...(!message['@id'] && id ? {'@id': `/api/messages/${id}`} : {}),
    ...(reactionsChanged ? {reactions} : {})} as ChatMessageApiModel;
}

export function matchesPendingMessage(pending: ChatMessageApiModel, confirmed: ChatMessageApiModel): boolean {
  return pending.localStatus === 'pending' && userKey(pending.sender) === userKey(confirmed.sender)
    && (pending.content || '') === (confirmed.content || '')
    && (pending.attachments?.length || 0) === (confirmed.attachments?.length || 0)
    && (!pending.createdAt || !confirmed.createdAt || Math.abs(+new Date(pending.createdAt) - +new Date(confirmed.createdAt)) < 120_000);
}

/** Server identity is independent of the API prefix and of message contents. */
export function chatMessageKey(message: ChatMessageApiModel): string {
  const id = message?.['@id']?.split('/').filter(Boolean).pop() || message?.id;
  if (id && !message.localStatus) return `/messages/${id}`;
  if (message?.clientTempId) return message.clientTempId;
  const sender = typeof message?.sender === 'string' ? message.sender.split('/').pop() : message?.sender?.id;
  return `${sender || ''}|${+new Date(message?.createdAt || 0)}|${message?.content || ''}|${message?.attachments?.length || 0}`;
}

export function mergeChatMessages(existing: ChatMessageApiModel[], incoming: ChatMessageApiModel[]): ChatMessageApiModel[] {
  const records = new Map(existing.map(normalizeChatMessage).map(message => [chatMessageKey(message), message]));
  incoming.map(normalizeChatMessage).forEach(message => {
    const key = chatMessageKey(message);
    const previous = records.get(key);
    const supplied = Object.fromEntries(Object.entries(message).filter(([, value]) => value !== undefined));
    const olderBody = previous && +new Date(previous.updatedAt || previous.createdAt || 0)
      > +new Date(message.updatedAt || message.createdAt || 0);
    const merged = previous ? (olderBody ? {...previous} : {...previous, ...supplied}) as ChatMessageApiModel : message;
    if (previous && (previous.reactionsVersion || 0) > (message.reactionsVersion ?? -1)) {
      merged.reactions = previous.reactions;
      merged.reactionsVersion = previous.reactionsVersion;
    } else if (message.reactions !== undefined) {
      merged.reactions = message.reactions;
      merged.reactionsVersion = message.reactionsVersion;
    }
    records.set(key, merged);
  });
  return Array.from(records.values()).sort((a, b) => +new Date(a.createdAt || 0) - +new Date(b.createdAt || 0)
    || chatMessageKey(a).localeCompare(chatMessageKey(b)));
}

export function currentChatMessageReaction(message: ChatMessageApiModel, user: {id?: number; '@id'?: string}) {
  return message.reactions?.find(reaction => {
    const author = reaction.author;
    return typeof author === 'string' ? author === user?.['@id']
      : (!!author?.id && author.id === user?.id) || (!!author?.['@id'] && author['@id'] === user?.['@id']);
  });
}
